import { useState, useEffect, useRef } from 'react'
import { Link } from 'react-router-dom'
import { useAuth0 } from '@auth0/auth0-react'
import { Header } from '@/components/layout/Header'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Badge } from '@/components/ui/badge'
import { useDocumentHead } from '@/hooks/useDocumentHead'
import { leagueService, type UserActiveLeague } from '@/services/leagueService'
import { Loader2, Trophy, Users, User, ChevronRight, Layers } from 'lucide-react'

export function MyLeagues() {
  const { isAuthenticated, isLoading: authLoading } = useAuth0()
  const [leagues, setLeagues] = useState<UserActiveLeague[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useDocumentHead({
    title: 'My Leagues',
    description: 'Your active league competitions',
  })

  const hasFetched = useRef(false)

  useEffect(() => {
    async function fetchLeagues() {
      if (!isAuthenticated || hasFetched.current) return
      hasFetched.current = true

      setLoading(true)
      setError(null)

      try {
        const data = await leagueService.listMyActiveLeagues()
        setLeagues(data)
      } catch (err) {
        console.error('Failed to load leagues:', err)
        setError('Failed to load your leagues')
        hasFetched.current = false // Allow retry on error
      } finally {
        setLoading(false)
      }
    }

    if (!authLoading) {
      fetchLeagues()
    }
  }, [isAuthenticated, authLoading])

  // Group leagues by group
  const leaguesByGroup = leagues.reduce((acc, league) => {
    if (!acc[league.groupId]) {
      acc[league.groupId] = {
        groupId: league.groupId,
        groupName: league.groupName,
        groupIcon: league.groupIcon,
        leagues: [],
      }
    }
    acc[league.groupId].leagues.push(league)
    return acc
  }, {} as Record<string, { groupId: string; groupName: string; groupIcon?: string; leagues: UserActiveLeague[] }>)

  const groupGroups = Object.values(leaguesByGroup)

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="container mx-auto px-4 py-8">
        <div className="flex items-center gap-3 mb-6">
          <div className="h-12 w-12 rounded-xl bg-primary/10 flex items-center justify-center">
            <Layers className="h-6 w-6 text-primary" />
          </div>
          <div>
            <h1 className="text-2xl font-bold">My Leagues</h1>
            <p className="text-muted-foreground">Your active league competitions</p>
          </div>
        </div>

        {/* Loading state */}
        {loading && (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        )}

        {/* Error state */}
        {error && (
          <Card className="border-destructive/50 bg-destructive/5">
            <CardContent className="pt-6">
              <p className="text-destructive text-center">{error}</p>
            </CardContent>
          </Card>
        )}

        {/* Empty state */}
        {!loading && !error && leagues.length === 0 && (
          <Card>
            <CardContent className="pt-6">
              <div className="text-center py-8">
                <Trophy className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
                <h3 className="text-lg font-medium mb-2">No Active Leagues</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  You're not currently participating in any active league competitions.
                </p>
                <Button asChild>
                  <Link to="/groups">Browse Your Groups</Link>
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* League list grouped by group */}
        {!loading && !error && groupGroups.length > 0 && (
          <div className="space-y-6">
            {groupGroups.map((group) => (
              <Card key={group.groupId}>
                <CardHeader className="pb-3">
                  <div className="flex items-center gap-3">
                    <Avatar className="h-10 w-10">
                      {group.groupIcon?.startsWith('data:') || group.groupIcon?.startsWith('https://') ? (
                        <AvatarImage src={group.groupIcon} alt={group.groupName} />
                      ) : null}
                      <AvatarFallback className="text-lg">
                        {group.groupIcon && !group.groupIcon.startsWith('data:') && !group.groupIcon.startsWith('https://') 
                          ? group.groupIcon 
                          : '🏸'}
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <CardTitle className="text-lg">
                        <Link 
                          to={`/group/${group.groupId}`}
                          className="hover:underline"
                        >
                          {group.groupName}
                        </Link>
                      </CardTitle>
                      <CardDescription>
                        {group.leagues.length} active league{group.leagues.length !== 1 ? 's' : ''}
                      </CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="pt-0">
                  <div className="divide-y">
                    {group.leagues.map((league) => (
                      <Link
                        key={`${league.leagueId}-${league.bracketId}`}
                        to={`/group/${league.groupId}/league/${league.leagueId}`}
                        className="flex items-center justify-between py-3 hover:bg-muted/50 -mx-3 px-3 rounded-lg transition-colors"
                      >
                        <div className="flex items-center gap-3 min-w-0">
                          <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                            <Trophy className="h-5 w-5 text-primary" />
                          </div>
                          <div className="min-w-0">
                            <p className="font-medium truncate">{league.leagueName}</p>
                            <div className="flex items-center gap-2 text-sm text-muted-foreground">
                              <span>{league.seasonName}</span>
                              <span>•</span>
                              <span className="truncate">{league.bracketName}</span>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <Badge variant="outline" className="hidden sm:flex">
                            {league.leagueFormat === 'singles' ? (
                              <><User className="h-3 w-3 mr-1" /> Singles</>
                            ) : (
                              <><Users className="h-3 w-3 mr-1" /> Doubles</>
                            )}
                          </Badge>
                          <ChevronRight className="h-5 w-5 text-muted-foreground" />
                        </div>
                      </Link>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>
    </div>
  )
}
